import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { AccountsProjectForm } from '../components/AccountsProjectForm';
import pb from '../lib/pocketbaseClient';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const records = await pb.collection('accounts_projects').getFullList({ 
        sort: '-created',
        expand: 'clientId'
      });

      if (records && records.length > 0) {
        const [expenses, vendorPayments, clientPayments] = await Promise.all([
          pb.collection('project_expenses').getFullList({ filter: 'project != ""' }),
          pb.collection('vendor_payments').getFullList({ filter: 'project != ""' }),
          pb.collection('client_payments').getFullList({ filter: 'project != ""' }),
        ]);

        const projectTotals = {};
        expenses.forEach(e => {
          if (!projectTotals[e.project]) projectTotals[e.project] = { cost: 0, revenue: 0 };
          projectTotals[e.project].cost += e.amount || 0;
        });
        vendorPayments.forEach(v => {
          if (!projectTotals[v.project]) projectTotals[v.project] = { cost: 0, revenue: 0 };
          projectTotals[v.project].cost += v.amount || 0;
        });
        clientPayments.forEach(c => {
          if (!projectTotals[c.project]) projectTotals[c.project] = { cost: 0, revenue: 0 };
          projectTotals[c.project].revenue += c.amount || 0;
        });

        const projectsWithFinancials = records.map(p => {
          const totals = projectTotals[p.id] || { cost: 0, revenue: 0 };
          return {
            ...p,
            totalCost: totals.cost,
            revenue: totals.revenue,
            profit: totals.revenue - totals.cost,
            clientName: p.expand?.clientId?.name || 'N/A',
          };
        });
        setProjects(projectsWithFinancials);
      } else {
        setProjects([]);
      }
    } catch (err) {
      console.warn('Could not fetch projects:', err);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      'upcoming': { label: '🔜 Upcoming', variant: 'info' },
      'ongoing': { label: '🟢 Ongoing', variant: 'success' },
      'completed': { label: '✅ Completed', variant: 'success' },
      'on-hold': { label: '⏸️ On Hold', variant: 'warning' },
    };
    const s = map[status] || { label: status, variant: 'default' };
    return <Badge variant={s.variant}>{s.label}</Badge>;
  };

  const getTypeBadge = (type) => {
    if (type === 'construction') return <Badge variant="info">🏗️ Construction</Badge>;
    if (type === 'interior') return <Badge variant="purple">🛋️ Interior</Badge>;
    return <Badge>{type}</Badge>;
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    return `₹${amount.toLocaleString()}`;
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name?.toLowerCase().includes(search.toLowerCase()) ||
                          project.clientName?.toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType === 'All' || project.type === filterType;
    const matchesStatus = filterStatus === 'All' || project.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleSaveProject = () => {
    setShowModal(false);
    setEditingProject(null);
    fetchProjects();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">📊 Projects</h1>
          <p className="text-sm text-slate-500">Manage all projects</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => { setEditingProject(null); setShowModal(true); }}>
          + New Project
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Total Projects</p><p className="text-2xl font-bold text-slate-800">{projects.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Ongoing</p><p className="text-2xl font-bold text-blue-600">{projects.filter(p => p.status === 'ongoing').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Completed</p><p className="text-2xl font-bold text-emerald-600">{projects.filter(p => p.status === 'completed').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase font-semibold">Upcoming</p><p className="text-2xl font-bold text-amber-600">{projects.filter(p => p.status === 'upcoming').length}</p></CardContent></Card>
      </div>

      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="flex-1 min-w-[200px]">
          <Input placeholder="🔍 Search projects or client..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full" />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
          <option value="All">All Types</option>
          <option value="construction">🏗️ Construction</option>
          <option value="interior">🛋️ Interior</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400">
          <option value="All">All Status</option>
          <option value="upcoming">🔜 Upcoming</option>
          <option value="ongoing">🟢 Ongoing</option>
          <option value="completed">✅ Completed</option>
          <option value="on-hold">⏸️ On Hold</option>
        </select>
        <Button variant="outline" onClick={() => { setSearch(''); setFilterType('All'); setFilterStatus('All'); }}>Clear Filters</Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-700">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wider">Project Name</th>
                <th className="px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wider">Client</th>
                <th className="px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</th>
                <th className="px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Work Order</th>
                <th className="px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Cost</th>
                <th className="px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">Revenue</th>
                <th className="px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wider text-right">P/L</th>
                <th className="px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wider text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredProjects.length === 0 ? (
                <tr><td colSpan="9" className="px-6 py-8 text-center text-slate-500">No projects found.</td></tr>
              ) : (
                filteredProjects.map((project) => (
                  <tr key={project.id} className="hover:bg-slate-50/50 transition">
                    <td className="px-4 py-2 font-medium text-blue-600 hover:text-amber-600 hover:underline">
                      <Link to={`/projects/${project.id}`} className="hover:text-amber-600">
                        {project.name}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-slate-600">{project.clientName}</td>
                    <td className="px-4 py-2">{getTypeBadge(project.type)}</td>
                    <td className="px-4 py-2">{getStatusBadge(project.status)}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatCurrency(project.workOrderValue || 0)}</td>
                    <td className="px-4 py-2 text-right font-mono text-slate-600">{formatCurrency(project.totalCost || 0)}</td>
                    <td className="px-4 py-2 text-right font-mono text-emerald-600">{formatCurrency(project.revenue || 0)}</td>
                    <td className={`px-4 py-2 text-right font-bold ${(project.profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {(project.profit || 0) >= 0 ? '+' : ''}{formatCurrency(project.profit || 0)}
                    </td>
                    <td className="px-4 py-2 text-center space-x-1">
                      <Link to={`/projects/${project.id}`} className="text-blue-500 hover:text-blue-700 text-base" title="View">
                        👁️
                      </Link>
                      <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 p-0 h-auto" onClick={() => { setEditingProject(project); setShowModal(true); }}>
                        ✏️
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400 text-center">Showing {filteredProjects.length} of {projects.length} projects</p>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <AccountsProjectForm 
              project={editingProject} 
              onSave={handleSaveProject} 
              onCancel={() => { setShowModal(false); setEditingProject(null); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
